import React, { useMemo, useState } from 'react';
import useFirestore from '../../hooks/useFirestore';
import { addItem, updateItem, deleteItem } from '../../firebase/firestore';
import Table from '../../components/Table/Table';
import Modal from '../../components/Modal/Modal';
import styles from './Products.module.css';

const emptyForm = { name: '', category: '', price: '', stock: '', image: '' };

export default function Products() {
  const { data: products, loading } = useFirestore('products');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('الكل');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ['الكل', ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.name?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = category === 'الكل' || p.category === category;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, category]);

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      category: product.category || '',
      price: product.price || '',
      stock: product.stock || '',
      image: product.image || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      await deleteItem('products', id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
    };
    try {
      if (editingId) {
        await updateItem('products', editingId, payload);
      } else {
        await addItem('products', payload);
      }
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'المنتج',
      render: (row) => (
        <div className={styles.productCell}>
          <div className={styles.thumb}>
            {row.image ? <img src={row.image} alt={row.name} /> : '📦'}
          </div>
          <span>{row.name}</span>
        </div>
      ),
    },
    { key: 'category', label: 'التصنيف' },
    { key: 'price', label: 'السعر', render: (row) => <span className="nums">{row.price} ج.م</span> },
    {
      key: 'stock',
      label: 'المخزون',
      render: (row) => (
        <span className={row.stock > 0 ? styles.inStock : styles.outStock}>
          {row.stock > 0 ? `${row.stock} متاح` : 'نفدت الكمية'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className={styles.actions}>
          <button onClick={() => openEditModal(row)} className={styles.editBtn}>تعديل</button>
          <button onClick={() => handleDelete(row.id)} className={styles.deleteBtn}>حذف</button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>المنتجات</h2>
          <p>إدارة كتالوج منتجاتك ({products.length} منتج)</p>
        </div>
        <button className={styles.addBtn} onClick={openAddModal}>+ إضافة منتج</button>
      </div>

      <div className={styles.filters}>
        <input
          className={styles.searchInput}
          placeholder="ابحث عن منتج..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select}>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <Table columns={columns} data={filtered} loading={loading} emptyText="لا توجد منتجات مطابقة" />

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'تعديل المنتج' : 'إضافة منتج جديد'}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label>اسم المنتج</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>التصنيف</label>
            <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>السعر</label>
              <input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>الكمية بالمخزون</label>
              <input type="number" required value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
            </div>
          </div>
          <div className={styles.field}>
            <label>رابط الصورة (اختياري)</label>
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
          </div>
          <button type="submit" className={styles.submitBtn} disabled={saving}>
            {saving ? 'جارِ الحفظ...' : editingId ? 'حفظ التعديلات' : 'إضافة المنتج'}
          </button>
        </form>
      </Modal>
    </div>
  );
}
